import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, TrashIcon } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { SlackIcon } from "@/components/icons/slack-icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useCompleteTask,
  useDeleteTask,
  useReassignTask,
  useSnoozeTask,
} from "@/features/tasks/api/use-tasks";
import { TaskReassignButton } from "@/features/tasks/components/task-reassign-button";
import { TaskSnoozeDropdown } from "@/features/tasks/components/task-snooze-dropdown";
import { TaskText } from "@/features/tasks/components/task-text";
import { getTaskCategoryById } from "@/features/tasks/lib/task-categories";
import {
  DEFAULT_CATEGORY_ID,
  formatTaskDueLabel,
  isTaskOverdue,
  markTaskCompleted,
  removeTask,
  setTaskDueDate,
  sortTasksByDueDate,
} from "@/features/tasks/lib/tasks-utils";
import { useCurrentUser } from "@/hooks/use-current-user";
import { initialsOf } from "@/lib/initials";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import adminAPI from "@/services/api";

export type ClaimTaskItem = Awaited<ReturnType<typeof adminAPI.listClaimTasks>>[number];

/** Claim-scoped task list, derived from the claim detail key. */
function claimTasksKey(claimId: string) {
  return [...queryKeys.claims.detail(claimId), "tasks"] as const;
}

/**
 * The widget reads its own claim-scoped query, which the task mutations' optimistic pass
 * does not cover: patch it here so a click moves the row at once, not after the round trip.
 */
function patchClaimTasks(
  queryClient: QueryClient,
  claimId: string,
  patch: (tasks: ClaimTaskItem[]) => ClaimTaskItem[],
) {
  queryClient.setQueryData<ClaimTaskItem[]>(claimTasksKey(claimId), (tasks) =>
    tasks ? patch(tasks) : tasks,
  );
}

/** Critically damped: rows settle into place, nothing here was thrown. */
const ROW_SPRING = { type: "spring", bounce: 0, duration: 0.32 } as const;
const REDUCED_TRANSITION = { duration: 0.15 } as const;

/** Port of Task/ClaimTasks.vue — « Tâches en cours » widget of the claim overview. */
export function ClaimTasks({ claimId }: { claimId: string }) {
  const queryClient = useQueryClient();
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);

  const { data: tasks = [] } = useQuery({
    queryKey: claimTasksKey(claimId),
    queryFn: () => adminAPI.listClaimTasks(claimId),
  });

  const activeTasks = sortTasksByDueDate(tasks.filter((task) => !task.completedAt));
  const overdueTasks = activeTasks.filter((task) => isTaskOverdue(task));
  const upcomingTasks = activeTasks.filter((task) => !isTaskOverdue(task));
  const completedTasks = sortTasksByDueDate(tasks.filter((task) => task.completedAt));

  // Rien en cours : pas de widget, les tâches terminées restent visibles depuis /tasks.
  if (!activeTasks.length) {
    return null;
  }

  const sectionProps = {
    claimId,
    onChanged: () => void queryClient.invalidateQueries({ queryKey: claimTasksKey(claimId) }),
  };

  return (
    <div className="px-4 py-4 text-left sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-3.5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            Tâches en cours
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600">
              {activeTasks.length}
            </span>
          </h2>
          {completedTasks.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500"
              aria-expanded={showCompletedTasks}
              onClick={() => setShowCompletedTasks(!showCompletedTasks)}
            >
              {showCompletedTasks ? <ChevronUpIcon /> : <ChevronDownIcon />}
              Tâches terminées ({completedTasks.length})
            </Button>
          ) : null}
        </div>

        {/* One layout group: a snoozed task glides from « En retard » into « À venir » instead of jumping. */}
        <LayoutGroup id={`claim-tasks-${claimId}`}>
          <TaskSection label="En retard" tone="overdue" tasks={overdueTasks} {...sectionProps} />
          <TaskSection label="À venir" tone="neutral" tasks={upcomingTasks} {...sectionProps} />
          {showCompletedTasks ? (
            <TaskSection label="Terminées" tone="neutral" tasks={completedTasks} {...sectionProps} />
          ) : null}
        </LayoutGroup>
      </div>
    </div>
  );
}

interface TaskSectionProps {
  label: string;
  tone: "overdue" | "neutral";
  tasks: ClaimTaskItem[];
  claimId: string;
  onChanged: () => void;
}

function TaskSection({ label, tone, tasks, claimId, onChanged }: TaskSectionProps) {
  const reduceMotion = useReducedMotion();

  if (!tasks.length) {
    return null;
  }

  return (
    <motion.section layout="position" transition={reduceMotion ? REDUCED_TRANSITION : ROW_SPRING}>
      <h3
        className={cn(
          "flex items-center gap-2 border-t border-gray-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide",
          tone === "overdue" ? "bg-red-50 text-red-700" : "bg-gray-50 text-gray-500",
        )}
      >
        {tone === "overdue" ? <span className="size-1.5 rounded-full bg-red-500" aria-hidden="true" /> : null}
        {label} · <span className="tabular-nums">{tasks.length}</span>
      </h3>
      <ul className="border-t border-gray-100">
        <AnimatePresence initial={false}>
          {tasks.map((task) => (
            <ClaimTaskRow key={task.id} task={task} claimId={claimId} onChanged={onChanged} />
          ))}
        </AnimatePresence>
      </ul>
    </motion.section>
  );
}

/** Shape of GET /admin/admins cached by AdminCombobox — the reassign picker already loaded it. */
type CachedAdmin = { id: string; fullName: string; profilePictureUrl?: string | null };

function ClaimTaskRow({
  task,
  claimId,
  onChanged,
}: {
  task: ClaimTaskItem;
  claimId: string;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();
  const { can } = useCurrentUser();
  const completeTask = useCompleteTask();
  const snoozeTask = useSnoozeTask();
  const reassignTask = useReassignTask();
  const deleteTask = useDeleteTask();

  // Same gate as the API (and as task creation).
  const canReassign = can([...UPCLAIM_ROLES, "LAWYER"]);
  const overdue = isTaskOverdue(task);
  const dueDate = new Date(task.dueDate);
  const categoryName = getTaskCategoryById(task.categoryId || DEFAULT_CATEGORY_ID)?.displayName;
  const assigneeName = task.assignedAdmin.fullName;

  // Patch first, then call: the row answers the click on the same frame. The refetch on
  // settle reconciles either way, and brings the row back if the call failed.
  function complete() {
    patchClaimTasks(queryClient, claimId, (tasks) => markTaskCompleted(tasks, task.id));
    completeTask.mutate({ claimId, taskId: task.id }, { onSettled: onChanged });
  }

  function snooze(newDueDate: Date) {
    patchClaimTasks(queryClient, claimId, (tasks) => setTaskDueDate(tasks, task.id, newDueDate));
    snoozeTask.mutate({ claimId, taskId: task.id, newDueDate }, { onSettled: onChanged });
  }

  function reassign(assignedAdminId: string) {
    const assignee = queryClient
      .getQueryData<CachedAdmin[]>(queryKeys.search.admins)
      ?.find((admin) => admin.id === assignedAdminId);
    if (assignee) {
      patchClaimTasks(queryClient, claimId, (tasks) =>
        tasks.map((item) =>
          item.id === task.id
            ? {
                ...item,
                assignedAdminId,
                assignedAdmin: {
                  ...item.assignedAdmin,
                  id: assignee.id,
                  fullName: assignee.fullName,
                  profilePictureUrl: assignee.profilePictureUrl ?? null,
                },
              }
            : item,
        ),
      );
    }
    reassignTask.mutate({ claimId, taskId: task.id, assignedAdminId }, { onSettled: onChanged });
  }

  function remove() {
    patchClaimTasks(queryClient, claimId, (tasks) => removeTask(tasks, task.id));
    deleteTask.mutate({ claimId, taskId: task.id }, { onSettled: onChanged });
  }

  return (
    <motion.li
      layout="position"
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, height: "auto" }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
      transition={reduceMotion ? REDUCED_TRANSITION : ROW_SPRING}
      className="group overflow-hidden border-b border-gray-100 last:border-b-0"
    >
      <div className="px-4 py-3 transition-colors group-focus-within:bg-gray-50 group-hover:bg-gray-50">
        {/* One line: who it is for, what it is, when it is due — with the actions floating over
            the due date. */}
        <div className="relative flex items-center gap-3">
          <Avatar className="size-9 shrink-0 bg-gray-100 text-gray-600">
            {task.assignedAdmin.profilePictureUrl ? (
              <AvatarImage src={task.assignedAdmin.profilePictureUrl} alt={assigneeName} />
            ) : null}
            <AvatarFallback title={assigneeName}>{initialsOf(assigneeName)}</AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <p
              className={cn(
                "text-sm font-medium leading-snug text-gray-900",
                task.completedAt && "font-normal text-gray-500 line-through",
              )}
            >
              {task.title}
            </p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
              <span>{[categoryName, assigneeName].filter(Boolean).join(" · ")}</span>
              {task.slackReminderId && !task.completedAt ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className="inline-flex text-gray-400" aria-label="Rappel Slack programmé" />
                    }
                  >
                    <SlackIcon className="size-3" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Rappel Slack à {assigneeName} le{" "}
                    {format(dueDate, "EEEE d MMMM 'à' HH:mm", { locale: fr })}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </p>
          </div>

          <div className="shrink-0 text-right">
            {task.completedAt ? (
              <Badge variant="success" size="sm">
                Terminée
              </Badge>
            ) : (
              <p className={cn("text-sm font-semibold", overdue ? "text-red-700" : "text-gray-900")}>
                {formatTaskDueLabel(dueDate)}
              </p>
            )}
            <time dateTime={dueDate.toISOString()} className="block text-xs tabular-nums text-gray-500">
              {format(dueDate, "EEEE d MMMM · HH:mm", { locale: fr })}
            </time>
          </div>

          {/* With a mouse the actions reserve no width: they float over the due date on hover or
              keyboard focus, and stay while the snooze menu is open. On touch they sit in the row. */}
          <div
            className={cn(
              "flex shrink-0 items-center gap-0.5 transition-opacity",
              "pointer-fine:pointer-events-none pointer-fine:absolute pointer-fine:inset-y-0 pointer-fine:right-0 pointer-fine:bg-gradient-to-r pointer-fine:from-transparent pointer-fine:via-gray-50 pointer-fine:via-25% pointer-fine:to-gray-50 pointer-fine:pl-10 pointer-fine:pr-3 pointer-fine:opacity-0",
              "pointer-fine:group-hover:pointer-events-auto pointer-fine:group-hover:opacity-100",
              "pointer-fine:group-focus-within:pointer-events-auto pointer-fine:group-focus-within:opacity-100",
              "pointer-fine:has-data-popup-open:pointer-events-auto pointer-fine:has-data-popup-open:opacity-100",
            )}
          >
            {!task.completedAt ? (
              <>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-gray-500 hover:text-emerald-600"
                        aria-label="Compléter la tâche"
                        onClick={complete}
                      />
                    }
                  >
                    <CheckIcon />
                  </TooltipTrigger>
                  <TooltipContent>Compléter</TooltipContent>
                </Tooltip>
                <TaskSnoozeDropdown ghost onSnooze={snooze} currentDueDate={task.dueDate} />
                {canReassign ? <TaskReassignButton task={task} onReassign={reassign} /> : null}
              </>
            ) : null}
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-gray-500 hover:text-red-600"
                    aria-label="Supprimer la tâche"
                    onClick={remove}
                  />
                }
              >
                <TrashIcon />
              </TooltipTrigger>
              <TooltipContent>Supprimer</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* The context gets the row's whole width: squeezed into the title column it wrapped every
            few words. */}
        <TaskText text={task.taskText} className="mt-2" />
      </div>
    </motion.li>
  );
}
